import {ApexOptions} from "apexcharts";
import moment from "moment-timezone";
import ReactApexChart from "react-apexcharts";

import {useCurrentUserContext} from "@/components/context/hooks.ts";
import {autoDuration, shortDateTime} from "@/lib/format.tsx";
import {DataPoint} from "@/lib/types/checks.ts";

export interface ResponseTimeChartProps {
  data: DataPoint[];
  responseTimeSLA: string;
  min?: string;
  max?: string;
  isLarge?: boolean;
  width?: number;
  height?: number;
  tooltipLabel?: string;
}

const STATUS = {
  SUCCESS: "success",
  WARNING: "warning",
  DANGER: "danger",
};

const COLORS = {
  [STATUS.SUCCESS]: "124, 180, 236",
  [STATUS.WARNING]: "245, 180, 73",
  [STATUS.DANGER]: "229, 114, 110",
};

const responseTimeCSSClass = function (value: number, responseTimeSLA: number) {
  if (value <= responseTimeSLA * 0.7) {
    return STATUS.SUCCESS;
  } else if (value <= responseTimeSLA) {
    return STATUS.WARNING;
  } else {
    return STATUS.DANGER;
  }
};

export const ResponseTimeChart = ({
  data,
  responseTimeSLA,
  min,
  max,
  isLarge = false,
  width,
  height = 55,
  tooltipLabel = "Response Time",
}: ResponseTimeChartProps) => {
  const {user} = useCurrentUserContext();
  let selectedColor: string = STATUS.SUCCESS;
  if (data.length > 0 && responseTimeSLA !== null) {
    selectedColor = responseTimeCSSClass(data[data.length - 1].value, parseFloat(responseTimeSLA));
  }
  const series = [
    {
      name: tooltipLabel,
      data: data.map((x) => ({
        x: moment.utc(x.timestamp).unix(),
        y: parseFloat((x.value * 1000.0).toFixed(2)),
      })),
    },
  ];
  const options: ApexOptions = {
    chart: {
      type: "area",
      toolbar: {
        show: false,
      },
      zoom: {
        enabled: false,
      },
      sparkline: {
        enabled: !isLarge,
      },
      animations: {
        enabled: false,
      },
    },
    fill: {
      colors: [`rgba(${COLORS[selectedColor]}, 0.25)`],
      type: "solid",
    },
    colors: [`rgba(${COLORS[selectedColor]}, 0.75)`],
    dataLabels: {
      enabled: false,
    },
    xaxis: {
      tickAmount: 5,
      type: "datetime",
      labels: {
        show: isLarge,
        formatter: (v: string) =>
          shortDateTime(
            moment.unix(parseInt(v)).toISOString(),
            user!.current_date_format,
            user!.current_time_format,
            user!.account!.timezone
          ),
      },
      axisTicks: {
        show: isLarge,
      },
      axisBorder: {
        show: isLarge,
      },
      min: undefined,
      max: undefined,
    },
    yaxis: {
      tickAmount: 5,
      labels: {
        show: isLarge,
        formatter: (v: number) => autoDuration(v / 1000),
      },
      axisTicks: {
        show: isLarge,
      },
      axisBorder: {
        show: isLarge,
      },
    },
    grid: {
      show: true,
      xaxis: {
        lines: {
          show: true,
        },
      },
      yaxis: {
        lines: {
          show: true,
        },
      },
    },
    legend: {
      show: true,
    },
    tooltip: {
      y: {
        formatter: (v: number) => autoDuration(v / 1000),
      },
      x: {
        formatter: (v: number) =>
          shortDateTime(
            moment.unix(v).toISOString(),
            user!.current_date_format,
            user!.current_time_format,
            user!.account!.timezone
          ),
      },
      fixed: {
        enabled: true,
        position: "topRight",
        offsetX: -140,
        offsetY: -60,
      },
    },
  };

  if (min && options.xaxis) {
    options.xaxis.min = moment.utc(min).unix();
  }
  if (max && options.xaxis) {
    options.xaxis.max = moment.utc(max).unix();
  }

  return (
    <ReactApexChart
      type="area"
      series={series}
      options={options}
      width={width ? width : "100%"}
      height={height ? height : "100%"}
    />
  );
};
